import React, { useCallback, useState } from 'react';
import { View } from 'react-native';
import { useQuery } from '@apollo/client';

import { GET_MY_PAY_CYCLE_HOURS, GET_MY_TIMESHEETS_PAGE } from '../../../common/graphql/people/timesheet';
import {
	formatRosterDate,
	formatShiftRange,
	getTodayDeviceRosterDate,
} from '../../../common/utils/people/roster';
import { useUserContext } from '../../../common/utils/context';
import Text from '../../components/text/Text';
import {
	Card,
	EmptyState,
	Screen,
	SecondaryButton,
	StatusBadge,
} from '../../design/components';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import PayCycleHoursCard from './components/PayCycleHoursCard';
import RosterRow from './components/RosterRow';
import {
	getTimesheetBadgeTone,
	ROSTER_DATE_FORMAT,
	TIMESHEET_PAGE_SIZE,
} from './rosterHelpers';

// The employee's recorded timesheets, newest first, loaded a page at a time.
const MyTimesheetsView = () => {
	const styles = useThemedStyles(createStyles);
	const { isAVC } = useUserContext();
	const [loadingMoreTimesheets, setLoadingMoreTimesheets] = useState(false);
	const [timesheetLoadError, setTimesheetLoadError] = useState('');

	const {
		data,
		loading,
		error,
		refetch,
		fetchMore,
	} = useQuery(GET_MY_TIMESHEETS_PAGE, {
		skip: !isAVC,
		variables: {
			page: 0,
			perPage: TIMESHEET_PAGE_SIZE,
		},
		fetchPolicy: 'cache-and-network',
	});
	// The card stays hidden while loading or on error; the list below works on its own.
	const { data: payCycleHoursData, refetch: refetchPayCycleHours } = useQuery(GET_MY_PAY_CYCLE_HOURS, {
		skip: !isAVC,
		variables: { date: getTodayDeviceRosterDate() },
		fetchPolicy: 'cache-and-network',
	});
	const payCycleHours = payCycleHoursData?.getMyPayCycleHours;
	const timesheetPage = data?.getMyTimesheetsPage || {};
	const timesheets = timesheetPage.items || [];
	const timesheetCount = Number(timesheetPage.count) || 0;
	const canLoadOlderTimesheets = timesheets.length < timesheetCount;

	const loadOlderTimesheets = useCallback(async () => {
		if (loadingMoreTimesheets || !canLoadOlderTimesheets) {
			return;
		}

		const nextPage = Math.ceil(timesheets.length / TIMESHEET_PAGE_SIZE);
		setLoadingMoreTimesheets(true);
		setTimesheetLoadError('');

		try {
			await fetchMore({
				variables: {
					page: nextPage,
					perPage: TIMESHEET_PAGE_SIZE,
				},
				updateQuery: (previousResult, { fetchMoreResult }) => {
					const previousPage = previousResult?.getMyTimesheetsPage || {};
					const nextPageResult = fetchMoreResult?.getMyTimesheetsPage;

					if (!nextPageResult) {
						return previousResult;
					}

					const timesheetById = new Map();
					[
						...(previousPage.items || []),
						...(nextPageResult.items || []),
					].forEach((timesheet) => {
						if (timesheet?.id && !timesheetById.has(timesheet.id)) {
							timesheetById.set(timesheet.id, timesheet);
						}
					});

					return {
						...previousResult,
						getMyTimesheetsPage: {
							...previousPage,
							...nextPageResult,
							items: Array.from(timesheetById.values()),
							count: nextPageResult.count ?? previousPage.count ?? 0,
						},
					};
				},
			});
		}
		catch (fetchError) {
			console.error('Older timesheets load failed', fetchError);
			setTimesheetLoadError('Unable to load older timesheets. Please try again.');
		}
		finally {
			setLoadingMoreTimesheets(false);
		}
	}, [
		canLoadOlderTimesheets,
		fetchMore,
		loadingMoreTimesheets,
		timesheets.length,
	]);

	return (
		<Screen
			backLabel="My roster"
			heading="My timesheets"
			subheading={timesheetCount > timesheets.length
				? `Your recorded hours. ${timesheets.length} of ${timesheetCount} shown.`
				: 'Your recorded hours.'}
			isLoading={loading && !data}
			error={error && !data ? 'Unable to load timesheets. Please try again.' : null}
			onRetry={() => {
				refetch();
				refetchPayCycleHours();
			}}
		>
			{payCycleHours ? <PayCycleHoursCard payCycleHours={payCycleHours} /> : null}
			{!timesheets.length ? (
				<Card>
					<EmptyState
						icon="clock"
						title="No timesheets yet"
						message="Shifts appear here after you clock out."
					/>
				</Card>
			) : (
				<View style={styles.list}>
					{timesheets.map(timesheet => (
						<RosterRow
							key={timesheet.id}
							title={formatRosterDate(timesheet.date, ROSTER_DATE_FORMAT)}
							details={[
								[
									formatShiftRange(timesheet.startMinutes, timesheet.endMinutes),
									Number(timesheet.breakMinutes) ? `Break ${timesheet.breakMinutes}m` : null,
								].filter(Boolean).join(' · '),
								timesheet.notes,
							]}
							right={(
								<StatusBadge
									label={timesheet.statusLabel}
									tone={getTimesheetBadgeTone(timesheet.status)}
									size="small"
								/>
							)}
						/>
					))}
				</View>
			)}
			{canLoadOlderTimesheets ? (
				<SecondaryButton
					label="Load older timesheets"
					onPress={loadOlderTimesheets}
					loading={loadingMoreTimesheets}
				/>
			) : null}
			{timesheetLoadError ? <Text style={styles.errorText}>{timesheetLoadError}</Text> : null}
		</Screen>
	);
};

const createStyles = ({ ui: { colors, space, type } }) => ({
	list: {
		gap: space.sm,
	},
	errorText: {
		...type.bodySmall,
		color: colors.danger,
	},
});

export default MyTimesheetsView;
